import { CONSENT_CATEGORIES, CONSENT_SERVICES } from './constants';

// A stand-in for Usercentrics, enabled with GATSBY_CONSENT_MOCK=1.
//
// Two reasons it exists. The real CMP does start on localhost, but it serves
// the published configuration only, so services still sitting in a draft are
// unknown to it; and no amount of panel access lets you test the "visitor has
// an ad blocker" path, which is the one that silently breaks gated embeds. The mock implements the same surface the adapter reads,
// so the code under test is the real code path, not a parallel one.
const MOCK_CATEGORIES = {
  [CONSENT_SERVICES.USERCENTRICS]: CONSENT_CATEGORIES.ESSENTIAL,
  [CONSENT_SERVICES.GOOGLE_TAG_MANAGER]: CONSENT_CATEGORIES.ESSENTIAL,
  [CONSENT_SERVICES.CLOUDINARY]: CONSENT_CATEGORIES.ESSENTIAL,
  [CONSENT_SERVICES.SENTRY]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.WEBFLOW_CDN]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.JQUERY]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.GOOGLE_FONTS]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.GOOGLE_AJAX]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.PIPEDRIVE]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.SLIDESHARE]: CONSENT_CATEGORIES.FUNCTIONAL,
  [CONSENT_SERVICES.GOOGLE_ANALYTICS]: CONSENT_CATEGORIES.ANALYTICS,
  [CONSENT_SERVICES.HOTJAR]: CONSENT_CATEGORIES.ANALYTICS,
  [CONSENT_SERVICES.MICROSOFT_CLARITY]: CONSENT_CATEGORIES.ANALYTICS,
  [CONSENT_SERVICES.FACEBOOK_PIXEL]: CONSENT_CATEGORIES.MARKETING,
  [CONSENT_SERVICES.GOOGLE_ADS]: CONSENT_CATEGORIES.MARKETING,
  [CONSENT_SERVICES.LINKEDIN_INSIGHT]: CONSENT_CATEGORIES.MARKETING,
  [CONSENT_SERVICES.YOUTUBE]: CONSENT_CATEGORIES.MARKETING,
  [CONSENT_SERVICES.LIVESTORM]: CONSENT_CATEGORIES.MARKETING,
};

const NAMES = Object.entries(CONSENT_SERVICES).reduce((names, [key, id]) => {
  if (!id) {
    return names;
  }

  return { ...names, [id]: key };
}, {});

const buildState = () =>
  Object.keys(MOCK_CATEGORIES).reduce(
    (state, id) => ({ ...state, [id]: MOCK_CATEGORIES[id] === 'essential' }),
    {},
  );

export const installConsentMock = () => {
  if (typeof window === 'undefined') {
    return;
  }

  let granted = buildState();
  let blocked = false;

  const emit = () => {
    window.dispatchEvent(new CustomEvent('UC_UI_CMP_EVENT'));
  };

  const install = () => {
    window.UC_UI = {
      isInitialized: () => !blocked,
      getServicesBaseInfo: () =>
        Object.keys(MOCK_CATEGORIES).map((id) => ({
          id,
          name: NAMES[id] || id,
          categorySlug: MOCK_CATEGORIES[id],
          consent: { status: Boolean(granted[id]) },
        })),
      acceptService: (id) => {
        granted = { ...granted, [id]: true };
        emit();

        return Promise.resolve();
      },
      showSecondLayer: () => {
        // eslint-disable-next-line no-console
        console.info('[consent mock] second layer would open here');
      },
    };
  };

  install();

  // Underscored on purpose: a dev-only handle that must not read like app API.
  // eslint-disable-next-line no-underscore-dangle
  window.__consent = {
    grant: (id) => {
      granted = { ...granted, [id]: true };
      emit();
    },
    revokeAll: () => {
      granted = buildState();
      emit();
    },
    acceptAll: () => {
      granted = Object.keys(MOCK_CATEGORIES).reduce(
        (state, id) => ({ ...state, [id]: true }),
        {},
      );
      emit();
    },
    // Reproduces an ad blocker: UC_UI never becomes available, so the store
    // has to time out into the unavailable state on its own.
    simulateBlocked: () => {
      blocked = true;
      delete window.UC_UI;
      emit();
    },
    state: () => ({ blocked, granted }),
  };

  // eslint-disable-next-line no-console
  console.info('[consent mock] active - use window.__consent to drive it');
};
